import { Link } from "react-router-dom";
import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { FlameIcon, SpinnerGapIcon, ChartLineUpIcon, ArrowBendUpRightIcon, StarIcon, TrendUpIcon, ClockIcon, RocketLaunchIcon } from "@phosphor-icons/react";
import { fetchCandleRadar, fetchEarlyRunners, fetchBreakoutRadar } from "../lib/api";
import { markCandlesSeen } from "../lib/useCandleAlerts";
import { fmtUsd } from "../lib/format";
import { QuickBuyLink } from "../components/TradeButton";
import { useWatchlist } from "../context/WatchlistContext";
import { useSeo } from "../lib/useSeo";

const GOLD = "#F0C000";
const NEON = "#4ADE80";

const ageTxt = (h) => {
  if (h == null) return "age ?";
  if (h < 1) return `${Math.max(1, Math.round(h * 60))}m old`;
  return `${h.toFixed(1)}h old`;
};
const pct = (v) => (v == null ? "—" : `${v >= 0 ? "+" : ""}${Math.round(v).toLocaleString()}%`);
const pctColor = (v) => (v == null ? "#888" : v >= 0 ? NEON : "#FF3366");

const HeatBar = ({ heat, color }) => (
  <div className="w-12 h-1.5 bg-white/10 rounded-full overflow-hidden shrink-0" title={`Heat ${heat}/100 — how hot it is right now`}>
    <div className="h-full rounded-full" style={{ width: `${Math.min(heat || 0, 100)}%`, background: color }} />
  </div>
);

const Row = ({ r, rank, accent }) => {
  const { isWatched, toggle } = useWatchlist();
  const watched = isWatched(r.mint);
  const launch = r.stage === "launch";
  return (
    <div
      data-testid={`candle-row-${r.mint}`}
      className="relative flex items-center gap-3 sm:gap-4 px-4 sm:px-5 py-3.5 border transition-colors hover:bg-white/[0.03]"
      style={{ borderColor: `${accent}33`, background: r.freshMint ? `linear-gradient(90deg, ${accent}14, transparent)` : rank <= 3 ? `linear-gradient(90deg, ${accent}0d, transparent)` : "transparent" }}
    >
      <div className="w-6 text-center font-heading font-black text-lg shrink-0" style={{ color: rank <= 3 ? accent : "#666" }}>{rank}</div>
      {r.image ? (
        <img src={r.image} alt="" className="w-9 h-9 object-cover border border-white/10 shrink-0" onError={(e) => (e.target.style.display = "none")} />
      ) : (
        <div className="w-9 h-9 flex items-center justify-center border shrink-0" style={{ borderColor: `${accent}55`, color: accent }}>
          {launch ? "🕯️" : <TrendUpIcon size={18} weight="bold" />}
        </div>
      )}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <Link to={`/coin/${r.mint}`} className="font-heading font-bold hover:text-neon transition-colors">${r.symbol || "token"}</Link>
          {r.freshMint && (
            <span className="font-mono text-[9px] px-1.5 py-0.5 font-bold animate-pulse" style={{ background: `${GOLD}22`, border: `1px solid ${GOLD}`, color: GOLD }}>🔥 FRESH MINT</span>
          )}
          {r.score != null && (
            <span className="font-mono text-[10px] px-1.5 py-0.5 font-bold" style={{ background: `${accent}18`, border: `1px solid ${accent}66`, color: accent }}>{r.score}/100</span>
          )}
        </div>
        <div className="font-mono text-[11px] text-muted-foreground truncate mt-0.5 flex items-center gap-2 flex-wrap">
          <span className="inline-flex items-center gap-0.5"><ClockIcon size={11} /> {ageTxt(r.ageHours)}</span>
          {launch ? (
            <span>· h6=h24 <span style={{ color: pctColor(r.h24) }}>{pct(r.h24)}</span> <span className="text-muted-foreground/50">(spread {r.spreadPct}%)</span></span>
          ) : (
            <span>· 24h <span style={{ color: pctColor(r.h24) }}>{pct(r.h24)}</span> · 6h <span style={{ color: pctColor(r.h6) }}>{pct(r.h6)}</span></span>
          )}
          <span>· 1h <span style={{ color: pctColor(r.h1) }}>{pct(r.h1)}</span>{!launch && r.rising ? " ↑" : ""}</span>
        </div>
      </div>
      <div className="text-right shrink-0 hidden sm:block">
        <div className="font-mono text-sm font-bold">{fmtUsd(r.marketCap)}</div>
        <div className="font-mono text-[9px] uppercase tracking-wider text-muted-foreground">mcap · {fmtUsd(r.liquidity)} liq</div>
      </div>
      <HeatBar heat={r.heat} color={accent} />
      <div className="flex items-center gap-1 shrink-0">
        <QuickBuyLink coin={{ address: r.mint, url: r.url }} testid={`candle-buy-${r.mint}`} />
        <button onClick={() => toggle(r.mint)} title={watched ? "Remove from watchlist" : "Save to watchlist"} data-testid={`candle-watch-${r.mint}`} className="p-1.5 transition-colors hover:bg-white/5">
          <StarIcon size={16} weight={watched ? "fill" : "regular"} color={watched ? "#F5A623" : "#666"} />
        </button>
        <Link to={`/coin/${r.mint}`} title="Open chart & Degen Score" className="p-1.5 text-muted-foreground hover:text-neon transition-colors" data-testid={`candle-chart-${r.mint}`}>
          <ChartLineUpIcon size={16} weight="bold" />
        </Link>
      </div>
    </div>
  );
};

const EarlyRow = ({ r, rank }) => {
  const { isWatched, toggle } = useWatchlist();
  const watched = isWatched(r.mint);
  const accent = GOLD;
  return (
    <div
      data-testid={`early-runner-row-${r.mint}`}
      className="relative flex items-start gap-3 sm:gap-4 px-4 sm:px-5 py-3.5 border transition-colors hover:bg-white/[0.03]"
      style={{ borderColor: "#F0C00033", background: rank === 1 ? "linear-gradient(90deg, #F0C00016, transparent)" : "transparent" }}
    >
      <div className="w-6 text-center font-heading font-black text-lg shrink-0 pt-0.5" style={{ color: rank <= 3 ? accent : "#666" }}>{rank}</div>
      {r.image ? (
        <img src={r.image} alt="" className="w-9 h-9 object-cover border border-white/10 shrink-0" onError={(e) => (e.target.style.display = "none")} />
      ) : (
        <div className="w-9 h-9 flex items-center justify-center border shrink-0" style={{ borderColor: `${accent}55`, color: accent }}><RocketLaunchIcon size={18} weight="fill" /></div>
      )}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <Link to={`/coin/${r.mint}`} className="font-heading font-bold hover:text-neon transition-colors">${r.symbol || "token"}</Link>
          <span className="font-mono text-[10px] px-1.5 py-0.5 font-bold" style={{ background: `${accent}18`, border: `1px solid ${accent}`, color: accent }}>EARLY {r.score}/100</span>
          <span className="inline-flex items-center gap-0.5 font-mono text-[11px] text-muted-foreground"><ClockIcon size={11} /> {ageTxt(r.ageHours)}</span>
          {r.themeMatch && <span className="font-mono text-[10px] px-1.5 py-0.5" style={{ background: "#4ADE8014", border: "1px solid #4ADE8055", color: NEON }}>🔥 {r.themeMatch}</span>}
        </div>
        <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1">
          {(r.reasons || []).map((reason, i) => (
            <span key={i} className="font-mono text-[10px] text-muted-foreground/90">• {reason}</span>
          ))}
        </div>
      </div>
      <div className="text-right shrink-0 hidden sm:block pt-0.5">
        <div className="font-mono text-sm font-bold">{fmtUsd(r.marketCap)}</div>
        <div className="font-mono text-[9px] uppercase tracking-wider text-muted-foreground">mcap · {fmtUsd(r.liquidity)} liq</div>
      </div>
      <div className="flex items-center gap-1 shrink-0 pt-0.5">
        <QuickBuyLink coin={{ address: r.mint, url: r.url }} testid={`early-runner-buy-${r.mint}`} />
        <button onClick={() => toggle(r.mint)} title={watched ? "Remove from watchlist" : "Save to watchlist"} data-testid={`early-runner-watch-${r.mint}`} className="p-1.5 transition-colors hover:bg-white/5">
          <StarIcon size={16} weight={watched ? "fill" : "regular"} color={watched ? "#F5A623" : "#666"} />
        </button>
        <Link to={`/coin/${r.mint}`} title="Open chart & Degen Score" className="p-1.5 text-muted-foreground hover:text-neon transition-colors" data-testid={`early-runner-chart-${r.mint}`}>
          <ChartLineUpIcon size={16} weight="bold" />
        </Link>
      </div>
    </div>
  );
};

const BreakoutRow = ({ r, rank }) => {
  const { isWatched, toggle } = useWatchlist();
  const watched = isWatched(r.mint);
  const accent = r.combo ? "#F0C000" : r.reclaim ? "#A78BFA" : "#FF7A00";
  return (
    <div
      data-testid={`breakout-row-${r.mint}`}
      className="relative flex items-center gap-3 sm:gap-4 px-4 sm:px-5 py-3.5 border transition-colors hover:bg-white/[0.03]"
      style={{ borderColor: `${accent}33`, background: r.combo ? `linear-gradient(90deg, ${accent}1f, transparent)` : rank === 1 ? `linear-gradient(90deg, ${accent}16, transparent)` : "transparent" }}
    >
      <div className="w-6 text-center font-heading font-black text-lg shrink-0" style={{ color: rank <= 3 ? accent : "#666" }}>{rank}</div>
      {r.image ? (
        <img src={r.image} alt="" className="w-9 h-9 object-cover border border-white/10 shrink-0" onError={(e) => (e.target.style.display = "none")} />
      ) : (
        <div className="w-9 h-9 flex items-center justify-center border shrink-0 text-lg" style={{ borderColor: `${accent}55`, color: accent }}>{r.combo ? "🏆" : r.reclaim ? "♻️" : "🚀"}</div>
      )}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <Link to={`/coin/${r.mint}`} className="font-heading font-bold hover:text-neon transition-colors">${r.symbol || "token"}</Link>
          {r.combo && (
            <span data-testid={`breakout-combo-${r.mint}`} title={`X handle @${r.handle} is High Confidence AND breaking out — the CYBERLEEK double-signal`} className="font-mono text-[9px] px-1.5 py-0.5 font-bold animate-pulse" style={{ background: `${GOLD}22`, border: `1px solid ${GOLD}`, color: GOLD }}>🏆 HIGHEST CONVICTION</span>
          )}
          {r.surge != null && (
            <span className="font-mono text-[10px] px-1.5 py-0.5 font-bold" style={{ background: `${accent}18`, border: `1px solid ${accent}`, color: accent }}>
              {r.reclaim ? "RECLAIM" : `VOL ${r.surge}×`}
            </span>
          )}
          {r.score != null && (
            <span className="font-mono text-[10px] px-1.5 py-0.5 font-bold" style={{ background: "#4ADE8014", border: "1px solid #4ADE8055", color: NEON }}>{r.score}/100</span>
          )}
        </div>
        <div className="font-mono text-[11px] text-muted-foreground truncate mt-0.5 flex items-center gap-2 flex-wrap">
          <span className="inline-flex items-center gap-0.5"><ClockIcon size={11} /> {ageTxt(r.ageHours)}</span>
          <span>· 6h <span style={{ color: pctColor(r.h6) }}>{pct(r.h6)}</span></span>
          <span>· buys <span style={{ color: r.buyRatio >= 55 ? NEON : "#888" }}>{r.buyRatio}%</span></span>
          <span>· vol {fmtUsd(r.volumeH24)}</span>
        </div>
        {r.timeline?.length > 0 && (
          <div className="flex items-center gap-1.5 mt-1.5" title="Why it fired — price move per window">
            {r.timeline.map((t) => (
              <div key={t.w} className="flex flex-col items-center px-1.5 py-0.5 border border-white/8 bg-black/20 min-w-[42px]" data-testid={`breakout-tl-${r.mint}-${t.w}`}>
                <span className="font-mono text-[8px] uppercase tracking-wider text-muted-foreground/70">{t.w}</span>
                <span className="font-mono text-[10px] font-bold" style={{ color: pctColor(t.chg) }}>{t.chg == null ? "—" : `${t.chg >= 0 ? "+" : ""}${Math.round(t.chg)}%`}</span>
              </div>
            ))}
          </div>
        )}
        {r.reasons?.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1.5" data-testid={`breakout-reasons-${r.mint}`}>
            {r.reasons.map((reason, i) => (
              <span key={i} className="font-mono text-[9px] px-1.5 py-0.5 border" style={{ borderColor: `${accent}44`, color: "#B7BCC4" }}>{reason}</span>
            ))}
          </div>
        )}
      </div>
      <div className="text-right shrink-0 hidden sm:block">
        <div className="font-mono text-sm font-bold">{fmtUsd(r.marketCap)}</div>
        <div className="font-mono text-[9px] uppercase tracking-wider text-muted-foreground">mcap · {fmtUsd(r.liquidity)} liq</div>
      </div>
      <HeatBar heat={r.heat} color={accent} />
      <div className="flex items-center gap-1 shrink-0">
        <QuickBuyLink coin={{ address: r.mint, url: r.url }} testid={`breakout-buy-${r.mint}`} />
        <button onClick={() => toggle(r.mint)} title={watched ? "Remove from watchlist" : "Save to watchlist"} data-testid={`breakout-watch-${r.mint}`} className="p-1.5 transition-colors hover:bg-white/5">
          <StarIcon size={16} weight={watched ? "fill" : "regular"} color={watched ? "#F5A623" : "#666"} />
        </button>
        <Link to={`/coin/${r.mint}`} title="Open chart & Degen Score" className="p-1.5 text-muted-foreground hover:text-neon transition-colors" data-testid={`breakout-chart-${r.mint}`}>
          <ChartLineUpIcon size={16} weight="bold" />
        </Link>
      </div>
    </div>
  );
};

const Section = ({ title, emoji, blurb, rows, accent, empty, testid }) => (
  <div className="space-y-3" data-testid={testid}>
    <div>
      <h2 className="font-heading font-black text-xl tracking-tight flex items-center gap-2">
        <span>{emoji}</span> {title} <span className="font-mono text-xs text-muted-foreground">{rows.length}</span>
      </h2>
      <p className="text-xs text-muted-foreground mt-1 leading-relaxed max-w-2xl">{blurb}</p>
    </div>
    {rows.length === 0 ? (
      <div className="border border-white/10 bg-card py-10 text-center text-muted-foreground font-mono text-xs px-6">{empty}</div>
    ) : (
      <div className="border border-white/5 divide-y divide-white/5">
        {rows.map((r, i) => <Row key={r.mint} r={r} rank={i + 1} accent={accent} />)}
      </div>
    )}
  </div>
);

export default function CandleRadar() {
  useSeo({
    title: "🕯️ Candle Radar — Catch Tokens by the Candle They're Forming | AlphaApeing",
    description: "Track meme coins by their candle: 🕯️ Launch Candle (h6≈h24, just launched into one candle) and 📈 Carry (diverged & still rising — the next one). Catch them in the first minutes.",
  });
  const { data, isLoading } = useQuery({ queryKey: ["candleRadar"], queryFn: fetchCandleRadar, refetchInterval: 45000 });
  const { data: earlyData } = useQuery({ queryKey: ["earlyRunners"], queryFn: fetchEarlyRunners, refetchInterval: 40000 });
  const { data: breakoutData } = useQuery({ queryKey: ["breakoutRadar"], queryFn: fetchBreakoutRadar, refetchInterval: 45000 });
  const launch = data?.launch || [];
  const carry = data?.carry || [];
  const early = earlyData?.runners || [];
  const breakout = breakoutData?.breakout || [];
  const reclaim = breakoutData?.reclaim || [];

  useEffect(() => {
    if (!data && !earlyData) return;
    markCandlesSeen([...launch, ...carry, ...early].map((r) => r.mint).filter(Boolean));
  }, [data, earlyData]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="max-w-[900px] mx-auto px-4 sm:px-6 py-6 space-y-8 animate-fade-up" data-testid="candle-radar-page">
      <div>
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 flex items-center justify-center text-black text-2xl" style={{ background: GOLD }}>🕯️</div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter leading-none">
            CANDLE <span style={{ color: GOLD }}>RADAR</span>
          </h1>
        </div>
        <p className="text-muted-foreground mt-3 text-sm leading-relaxed max-w-2xl">
          Track tokens by the <span className="text-white font-semibold">candle they're forming</span>. When{" "}
          <span style={{ color: GOLD }} className="font-semibold">h6 = h24</span> the token launched into that one candle — brand new, whole life in a single move.
          Then watch for the <span style={{ color: NEON }} className="font-semibold">h6 ≠ h24 version with velocity still rising</span> — the one that survived and keeps climbing.
          Ranked by live <span className="text-white">heat</span> to surface them in their first minutes.
        </p>
      </div>

      <Link to="/" data-testid="candle-radar-back" className="inline-flex items-center gap-1.5 text-xs font-mono text-muted-foreground hover:text-neon transition-colors">
        <ArrowBendUpRightIcon size={14} className="rotate-180" /> Back to the Radar
      </Link>

      {isLoading ? (
        <div className="flex justify-center py-20"><SpinnerGapIcon size={32} className="animate-spin" style={{ color: GOLD }} /></div>
      ) : (
        <>
          <div className="space-y-3" data-testid="early-runner-section">
            <div>
              <h2 className="font-heading font-black text-xl tracking-tight flex items-center gap-2">
                <span>🚀</span> Early Runners — could be today's runner <span className="font-mono text-xs text-muted-foreground">{early.length}</span>
              </h2>
              <p className="text-xs text-muted-foreground mt-1 leading-relaxed max-w-2xl">
                The rare one (only a few a day): a young token being bought up with <span className="text-white">accelerating real volume</span> and buyers in control — the day-runner pattern, caught in its first hour. Judged on volume &amp; buyers, not noisy price %.
              </p>
            </div>
            {early.length === 0 ? (
              <div className="border border-white/10 bg-card py-10 text-center text-muted-foreground font-mono text-xs px-6">
                No early runners forming right now. This is the highest-conviction, lowest-frequency signal — it stays quiet until a young token starts getting bought up hard.
              </div>
            ) : (
              <div className="border divide-y divide-white/5" style={{ borderColor: "#F0C00033" }}>
                {early.map((r, i) => <EarlyRow key={r.mint} r={r} rank={i + 1} />)}
              </div>
            )}
          </div>

          <div className="space-y-3" data-testid="breakout-section">
            <div>
              <h2 className="font-heading font-black text-xl tracking-tight flex items-center gap-2">
                <span>🚀</span> Breakout Radar — quality survivors exploding <span className="font-mono text-xs text-muted-foreground">{breakout.length}</span>
              </h2>
              <p className="text-xs text-muted-foreground mt-1 leading-relaxed max-w-2xl">
                The <span className="text-white font-semibold">CYBERLEEK pattern</span>: a token that already <span className="text-white">survived</span> (6h+ old, real liquidity &amp; socials) suddenly breaking out on a <span style={{ color: "#FF7A00" }} className="font-semibold">volume explosion</span> with buyers in control. Not a fresh launch — a proven coin catching a second wind.
              </p>
            </div>
            {breakout.length === 0 ? (
              <div className="border border-white/10 bg-card py-10 text-center text-muted-foreground font-mono text-xs px-6">
                No breakouts firing right now. A coin lands here when its recent volume runs far above its daily pace and it breaks out 40%+ with buy-dominance.
              </div>
            ) : (
              <div className="border divide-y divide-white/5" style={{ borderColor: "#FF7A0033" }}>
                {breakout.map((r, i) => <BreakoutRow key={r.mint} r={r} rank={i + 1} />)}
              </div>
            )}
          </div>

          {reclaim.length > 0 && (
            <div className="space-y-3" data-testid="reclaim-section">
              <div>
                <h2 className="font-heading font-black text-xl tracking-tight flex items-center gap-2">
                  <span>♻️</span> Second-Leg Reclaim <span className="font-mono text-xs text-muted-foreground">{reclaim.length}</span>
                </h2>
                <p className="text-xs text-muted-foreground mt-1 leading-relaxed max-w-2xl">
                  Coins that <span className="text-white">dumped 50%+</span> from a prior high and are now <span style={{ color: "#A78BFA" }} className="font-semibold">reclaiming with fresh volume</span> — CYBERLEEK's second explosion after its shakeout.
                </p>
              </div>
              <div className="border divide-y divide-white/5" style={{ borderColor: "#A78BFA33" }}>
                {reclaim.map((r, i) => <BreakoutRow key={r.mint} r={r} rank={i + 1} />)}
              </div>
            </div>
          )}

          <Section
            testid="candle-launch-section"
            title="Launch Candle" emoji="🕯️" accent={GOLD}
            blurb="h6 ≈ h24 — the token's entire tracked life is one candle since it launched. 🔥 Fresh Mint = under 30 minutes old."
            rows={launch}
            empty="No launch candles firing right now. A coin lands here when it's young and its 6h & 24h moves are the same green candle."
          />
          <Section
            testid="candle-carry-section"
            title="Carry — the next one" emoji="📈" accent={NEON}
            blurb="24h and 6h have diverged, the move is still positive, and velocity is rising this hour — it survived the first candle and is still going."
            rows={carry}
            empty="No carry signals right now. A coin lands here once it's past its launch candle but still climbing with fresh buyers."
          />
        </>
      )}

      <div className="flex items-center gap-2 text-[11px] font-mono text-muted-foreground border-t border-white/5 pt-4">
        <FlameIcon size={13} style={{ color: GOLD }} />
        Refreshes every 45s. Blends the trending + early-launch feeds. Community-flagged scams & copycat tickers auto-hidden. Signals, not certainties — not financial advice.
      </div>
    </div>
  );
}
